import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';

import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/styles.css';
import '../styles/formularios.css';
import logoInstituto from "../components/images/logo.png";
import logoSEP from "../components/images/SEP_Logo_2019.png";


import AOS from 'aos';
import 'aos/dist/aos.css';
import { useLocation, useNavigate } from 'react-router-dom';


const RegistrarCategoriaMateria = () => {
  const navigate = useNavigate();
    const location = useLocation();
    const handleSalir = () => {
      const rol = resolveRolBase(location.pathname);
      if (rol) {
        navigate(`/${rol}/inicio`, { replace: true });
      } else {
        // Fallback por si no se detecta rol ni en path ni en localStorage:
        navigate('/inicio', { replace: true });
      }
    };

  const [descripcion, setDescripcion] = useState('');
  const [siguienteId, setSiguienteId] = useState('Auto');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const apiBases = useMemo(() => {
    const baseList = [];
    const seen = new Set();
    const pushBase = (value) => {
      if (!value) return;
      const normalized = value.replace(/\/$/, '');
      if (!seen.has(normalized)) {
        seen.add(normalized);
        baseList.push(normalized);
      }
    };

    const pushWithApi = (value, { includeRaw = false } = {}) => {
      if (!value) return;
      const normalized = value.replace(/\/$/, '');
      if (includeRaw) pushBase(normalized);
      pushBase(`${normalized}/api`);
      pushBase(`${normalized}/backend`);
    };

    const envBase = process.env.REACT_APP_API_BASE?.trim();
    if (envBase) pushWithApi(envBase, { includeRaw: true });

    if (typeof window !== 'undefined') {
      const { origin } = window.location;
      pushWithApi(origin);
      pushWithApi('https://institutosilvianocarrillo.com');
      pushWithApi('https://www.institutosilvianocarrillo.com');
    }

    pushBase('/api');
    pushBase('/backend');

    return baseList.map((base) => `${base}/categorias`);
  }, []);

  const fetchJsonWithFallback = useCallback(async (suffix, options) => {
    let lastError = null;
    for (const base of apiBases) {
  const url = `${base}${suffix}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          lastError = new Error(text || `Error ${response.status}`);
          lastError.status = response.status;
          console.warn(`⚠️ Falló la petición a ${url}:`, lastError);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return trimmed ? JSON.parse(trimmed) : {};
        }

        lastError = new Error(`Respuesta inválida desde ${url}`);
        console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
      }
    }
    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  const cargarSiguienteId = useCallback(async () => {
    try {
      const data = await fetchJsonWithFallback('/siguiente-id');
      setSiguienteId(data?.siguienteId ?? 'Auto');
    } catch (error) {
      console.error('❌ Error al obtener siguiente ID:', error);
      setSiguienteId('Auto');
    }
  }, [fetchJsonWithFallback]);

  useEffect(() => {
    AOS.init();
    cargarSiguienteId();
  }, [cargarSiguienteId]);

  const insertarCategoria = async (e) => {
    e.preventDefault();
    if (!descripcion.trim()) {
      setMsg({ type: 'warning', text: 'Escribe una descripción.' });
      await Swal.fire({
        title: 'Atención',
        text: 'Escribe una descripción.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    setLoading(true);
    setMsg(null);
    try {
      let lastError = null;
      for (const base of apiBases) {
        const url = `${base}/registrar`;
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ descripcion: descripcion.trim() })
          });

          const text = await response.text();
          if (!response.ok) {
            lastError = new Error(text || `Error ${response.status}`);
            lastError.status = response.status;
            console.warn(`⚠️ Falló el POST a ${url}:`, lastError);
            continue;
          }

          await Swal.fire({
            title: 'Registrado',
            text: text || 'Categoría registrada correctamente',
            icon: 'success',
            confirmButtonText: 'OK',
            allowOutsideClick: false
          });

          setMsg({ type: 'success', text: 'Categoría registrada correctamente' });
          setDescripcion('');
          await cargarSiguienteId();
          return;
        } catch (error) {
          lastError = error;
          console.warn(`⚠️ Error de red al hacer POST ${url}:`, error);
        }
      }

      throw lastError || new Error('No se pudo registrar');
    } catch (err) {
      // Modal de error
      await Swal.fire({
        title: 'Error',
        text: err.message || 'No se pudo registrar',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      setMsg({ type: 'danger', text: err.message || 'No se pudo registrar' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="flex-grow-1 text-center">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Registrar Categoría de Materia</small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Mensaje inline (opcional) */}
      {msg && <div className={`alert alert-${msg.type} py-2`}>{msg.text}</div>}

      {/* Formulario */}
      <div className="p-4 bg-light rounded shadow-sm">
        <h6 className="mb-3">Categoría de Materia</h6>
        <form className="row g-3" onSubmit={insertarCategoria}>
          <div className="col-md-2">
            <label htmlFor="idCategoria" className="form-label">ID</label>
            <input
              type="text"
              className="form-control"
              id="idCategoria"
              placeholder="Auto"
              value={siguienteId}
              disabled
            />
          </div>

          <div className="col-md-10">
            <label htmlFor="descripcion" className="form-label">Descripción</label>
            <input
              type="text"
              className="form-control"
              id="descripcion"
              placeholder="Escribe la categoría..."
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </div>
        </form>
      </div>

      {/* Botones */}
      <div className="d-flex justify-content-center gap-4 mt-3">
        <button className="btn btn-success px-4" onClick={insertarCategoria} disabled={loading}>
          {loading ? 'Guardando...' : 'Registrar'}
        </button>
        <button type="button" className="btn btn-danger px-4" onClick={handleSalir}>
          Salir
        </button>
      </div>
    </div>
  );
};
/* ----------------- Helper para detectar el rol ----------------- */
function resolveRolBase(pathname) {
  // 1) Intenta desde la URL actual
  const roles = ['directora', 'coordinadora', 'docente', 'alumno'];
  const seg = pathname.split('/').filter(Boolean)[0];
  if (roles.includes(seg)) return seg;

  // 2) Respaldo desde localStorage.role (números mapeados a slugs)
  const roleId = Number(localStorage.getItem('role'));
  const map = { 1: 'directora', 2: 'coordinadora', 3: 'docente', 4: 'alumno' };
  return map[roleId] || null;
}

export default RegistrarCategoriaMateria;
